/**
 * aiPlanDocMapper — convierte el JSON que devuelve el modelo (Claude Haiku
 * 4.5 vía POST /aetherlite/ai/generate-plan) en un array de PlanSection que
 * el editor sabe renderizar.
 *
 * El modelo devuelve un objeto con claves `cover`, `coachBio`, `client`,
 * `routine`, `cardio`, `diet`, `supplements`, `notes` — cada una con el
 * shape esperado por la sección homónima.
 *
 * Estrategia: tomamos las secciones default (defaultPlan().sections) y
 * sobreescribimos `data` con el contenido del modelo. Si el modelo no
 * devuelve una sección (raro), conservamos el default. Si devuelve campos
 * extra, los ignoramos.
 *
 * El mapper también:
 *  - Saneo de IDs duplicados — si el modelo repite ids, los regeneramos.
 *  - Saneo de números: el modelo a veces devuelve `sets` como string,
 *    `rir` como string, etc. — coercionamos a number cuando aplica.
 *  - Fallbacks: si una sub-key falta, devolvemos el default vacío para que
 *    la UI no rompa.
 */
import {
  genId,
  type PlanSection,
  type SectionType,
} from "../types/plan";

/** Atajo para tipar el data field de cada section type. */
type DataOf<T extends SectionType> = Extract<PlanSection, { type: T }>["data"];

/**
 * Reemplaza el `data` de cada sección con el contenido devuelto por la IA.
 * Mantiene id/enabled/styles/decorations existentes — solo cambia el `data`.
 */
export function aiPlanDocToSections(
  baseSections: PlanSection[],
  aiDoc: any,
): PlanSection[] {
  if (!aiDoc || typeof aiDoc !== "object") return baseSections;

  return baseSections.map((sec): PlanSection => {
    const aiData = aiDoc[sec.type];
    if (!aiData || typeof aiData !== "object") return sec;

    switch (sec.type) {
      case "cover":
        return { ...sec, data: mergeCover(sec.data, aiData) };
      case "coachBio":
        return { ...sec, data: mergeCoachBio(sec.data, aiData) };
      case "client":
        return { ...sec, data: mergeClient(sec.data, aiData) };
      case "routine":
        return { ...sec, data: mergeRoutine(sec.data, aiData) };
      case "cardio":
        return { ...sec, data: mergeCardio(sec.data, aiData) };
      case "diet":
        return { ...sec, data: mergeDiet(sec.data, aiData) };
      case "supplements":
        return { ...sec, data: mergeSupplements(sec.data, aiData) };
      case "notes":
        return { ...sec, data: mergeNotes(aiData) };
      default:
        return sec;
    }
  });
}

// ----------------------------------------------------------------------
// Mergers por sección
// ----------------------------------------------------------------------

function mergeCover(base: DataOf<"cover">, ai: any): DataOf<"cover"> {
  return {
    ...base,
    eyebrow: str(ai.eyebrow, base.eyebrow),
    programTitle: str(ai.programTitle, base.programTitle),
    programSubtitle: str(ai.programSubtitle, base.programSubtitle),
    clientName: str(ai.clientName, base.clientName),
    duration: str(ai.duration, base.duration),
  };
}

function mergeCoachBio(base: DataOf<"coachBio">, ai: any): DataOf<"coachBio"> {
  return {
    ...base,
    bio: str(ai.bio, base.bio),
    methodology: str(ai.methodology, base.methodology),
  };
}

function mergeClient(base: DataOf<"client">, ai: any): DataOf<"client"> {
  return {
    ...base,
    fullName: str(ai.fullName, base.fullName),
    age: str(ai.age, base.age),
    weight: str(ai.weight, base.weight),
    height: str(ai.height, base.height),
    goal: str(ai.goal, base.goal),
    level: str(ai.level, base.level),
    injuries: str(ai.injuries, base.injuries),
  };
}

/** Si el modelo devolvió `weeks: []` significa que el coach DESACTIVÓ la
 *  generación de entrenamiento. En ese caso preservamos la rutina existente
 *  del plan en vez de pisarla con un default vacío. */
function mergeRoutine(base: DataOf<"routine">, ai: any): DataOf<"routine"> {
  const weeks = Array.isArray(ai.weeks) ? ai.weeks : [];
  if (weeks.length === 0) return base;
  return {
    weeks: weeks.map((w: any, wi: number) => ({
      id: str(w.id) || genId("w"),
      label: str(w.label, `Semana ${wi + 1}`),
      days: (Array.isArray(w.days) ? w.days : []).map((d: any, di: number) => ({
        id: str(d.id) || genId("d"),
        label: str(d.label, `Día ${di + 1}`),
        focus: str(d.focus, ""),
        warmup: str(d.warmup, ""),
        exercises: (Array.isArray(d.exercises) ? d.exercises : []).map((ex: any) => ({
          id: str(ex.id) || genId("ex"),
          name: str(ex.name, "Ejercicio"),
          sets: num(ex.sets, 3),
          reps: str(ex.reps, "10"),
          weight: str(ex.weight, ""),
          rest: str(ex.rest, "60s"),
          rir: num(ex.rir, 2),
          notes: str(ex.notes, ""),
        })),
      })),
    })),
  };
}

/** Cardio: si vino vacío (toggle entrenamiento OFF), preservar existente. */
function mergeCardio(base: DataOf<"cardio">, ai: any): DataOf<"cardio"> {
  const sessions = Array.isArray(ai.sessions) ? ai.sessions : [];
  const summary = str(ai.summary, "");
  if (sessions.length === 0 && !summary) return base;
  return {
    summary,
    sessions: sessions.map((s: any) => ({
      id: str(s.id) || genId("cs"),
      day: str(s.day, ""),
      type: str(s.type, ""),
      duration: str(s.duration, ""),
      intensity: str(s.intensity, ""),
      notes: str(s.notes, ""),
    })),
  };
}

function mergeDiet(base: DataOf<"diet">, ai: any): DataOf<"diet"> {
  const macros = ai.macros && typeof ai.macros === "object" ? ai.macros : {};
  const days = Array.isArray(ai.days) ? ai.days : [];
  // Si el modelo no generó días Y no devolvió macros con sentido, el coach
  // desactivó nutrición — preservar la dieta existente del plan.
  if (
    days.length === 0 &&
    !macros.kcal &&
    !macros.protein &&
    !ai.summary
  ) {
    return base;
  }
  return {
    ...base,
    summary: str(ai.summary, base.summary),
    macros: {
      kcal: str(macros.kcal, base.macros?.kcal || ""),
      protein: str(macros.protein, base.macros?.protein || ""),
      carbs: str(macros.carbs, base.macros?.carbs || ""),
      fat: str(macros.fat, base.macros?.fat || ""),
    },
    days: days.map((d: any, di: number) => ({
      id: str(d.id) || genId("dd"),
      label: str(d.label, `Día ${di + 1}`),
      meals: (Array.isArray(d.meals) ? d.meals : []).map((m: any) => ({
        id: str(m.id) || genId("ml"),
        name: str(m.name, "Comida"),
        foods: str(m.foods, ""),
        kcal: str(m.kcal, ""),
        p: str(m.p, ""),
        c: str(m.c, ""),
        f: str(m.f, ""),
        // Items estructurados con libId del food_library. El modelo los
        // devuelve desde la v2 para que la dieta sea editable con la
        // calculadora (escalar qty/baseQty recalcula macros). Si el plan
        // viene legacy sin items, el array queda vacío y el editor
        // funciona con `foods` (texto libre) como fallback.
        items: Array.isArray(m.items)
          ? (m.items as any[]).map((it) => ({
              id: str(it.id) || genId("fi"),
              libId: typeof it.libId === "number" ? it.libId : undefined,
              name: str(it.name, ""),
              qty: typeof it.qty === "number" && Number.isFinite(it.qty) ? it.qty : 0,
              unit: str(it.unit, "g"),
              baseQty: typeof it.baseQty === "number" && Number.isFinite(it.baseQty)
                ? it.baseQty
                : 100,
              kcal: typeof it.kcal === "number" ? it.kcal : Number(it.kcal) || 0,
              protein: typeof it.protein === "number" ? it.protein : Number(it.protein) || 0,
              carbs: typeof it.carbs === "number" ? it.carbs : Number(it.carbs) || 0,
              fat: typeof it.fat === "number" ? it.fat : Number(it.fat) || 0,
            }))
          : [],
      })),
    })),
  };
}

/** Supplements: si vino vacío Y sin summary, preservar el existente. */
function mergeSupplements(base: DataOf<"supplements">, ai: any): DataOf<"supplements"> {
  const items = Array.isArray(ai.items) ? ai.items : [];
  const summary = str(ai.summary, "");
  if (items.length === 0 && !summary) return base;
  return {
    summary,
    items: items.map((it: any) => ({
      id: str(it.id) || genId("sp"),
      name: str(it.name, "Suplemento"),
      dose: str(it.dose, ""),
      timing: str(it.timing, ""),
      notes: str(it.notes, ""),
    })),
  };
}

function mergeNotes(ai: any): DataOf<"notes"> {
  return {
    body: str(ai.body, ""),
    signature: str(ai.signature, ""),
  };
}

// ----------------------------------------------------------------------
// Coerciones
// ----------------------------------------------------------------------

function str(v: unknown, fallback: string = ""): string {
  if (v == null) return fallback;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return fallback;
}

function num(v: unknown, fallback: number): number | string {
  // El editor acepta number | string; preferimos number cuando es razonable,
  // y dejamos string si el modelo devolvió algo descriptivo (raro pero
  // posible: "AMRAP", "max").
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (!trimmed) return fallback;
    const n = Number(trimmed);
    if (Number.isFinite(n)) return n;
    return trimmed; // mantenemos texto descriptivo
  }
  return fallback;
}
